import { useEffect, useRef, useState } from 'react'
import Foco from 'react-foco'

import { Button } from 'components/Button'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { ReactComponent as PrivateIcon } from 'assets/icons/private16.svg'
import { ReactComponent as PublicIcon } from 'assets/icons/eye16.svg'
import { Copy } from 'almanac-ui/icons/16/Copy'

import { copyTextToClipboard } from 'utils/articleUtils'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { Permission } from 'types'
import { ShareSheetProps } from '../Types'
import Styles from '../ShareSheet.module.scss'

export const PasswordInput = ({ file }: ShareSheetProps) => {
  const { publicAccess, createPublicAccess, removePublicAccess, selectedPermission } = useShareSheetV2(file)
  const inputRef = useRef<HTMLInputElement>(null)
  const [toggleOn, setToggleOn] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showTogglePassword, setShowTogglePassword] = useState(!!publicAccess?.password)

  useEffect(() => {
    if (!inputRef?.current) return
    if (publicAccess?.password === undefined) return

    inputRef.current.value = publicAccess.password || ''
  }, [publicAccess?.password])

  useEffect(() => {
    setToggleOn(!!publicAccess)
  }, [publicAccess])

  const copyPassword = () => {
    if (!publicAccess?.password) return

    copyTextToClipboard(publicAccess.password)
    showToastMessage('Password copied to the clipboard!', 'success')
  }

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev)
  }

  const updatePublicAccess = (permission: Permission | null, password?: string | null) => {
    if (!toggleOn) setToggleOn(!!permission)
    const onError = () => {
      if (!selectedPermission) setToggleOn(!permission)
    }
    if (!!permission) {
      createPublicAccess(permission, onError, password)
      return
    }

    if (!!publicAccess) {
      removePublicAccess(publicAccess, onError)
    }
  }

  const handleUpdatePassword = () => {
    const modifiedPassword = [undefined, ''].includes(inputRef.current?.value) ? null : inputRef.current?.value

    if (modifiedPassword === publicAccess?.password) return

    updatePublicAccess(selectedPermission, modifiedPassword)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUpdatePassword()
    }
  }

  return (
    <Foco onClickOutside={handleUpdatePassword}>
      <div className={Styles.passwordInputContainer}>
        <input
          ref={inputRef}
          style={{
            maxWidth: '290px',
            minWidth: !!inputRef.current?.value?.length ? '130px' : '154px',
            width: `${inputRef?.current?.value?.length}ch`,
            marginRight: '4px',
          }}
          onChange={(e) => {
            setShowTogglePassword((prev) => !prev && !!e.target.value.length)
          }}
          type={showPassword ? 'text' : 'password'}
          placeholder="Add password"
          autoComplete="one-time-code"
          onKeyDown={handleKeyDown}
        />

        {(showTogglePassword || !!inputRef.current?.value?.length) && (
          <Button className={Styles.squareButton} variant="tertiary" size="md" onPress={toggleShowPassword}>
            {showPassword ? <PrivateIcon /> : <PublicIcon />}
          </Button>
        )}

        {!!publicAccess?.password && (
          <Button className={Styles.squareButton} variant="tertiary" size="md" onPress={copyPassword}>
            <Copy />
          </Button>
        )}
      </div>
    </Foco>
  )
}
